import { defineStore } from "pinia";

import type { BrowserNotificationState, LocalNotification } from "./interfaces";
import { StateNamespace } from "../App/Models/State/StateNamespace";
import { NOTIFICATION_PERMISSION } from "./interfaces";
import { NotificationType } from "@/Modules/App/Models/Domain/NotificationType";

import useUserStore from "@/Modules/User/Store";
import useChatStore from "@/Modules/Chat/Store";
import useAppStore from "../App/Store";

import icon from "@/Assets/images/logo.svg";
import sound from "./notification.mp3";

import { APP_ROUTES } from "@/Generated/Routes/paths";
import AppRouter from "@/Router";

import { hasValue } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";
import { log } from "@/Utils/Debug";

const state: BrowserNotificationState = {
  vibrate: true,
  iconPath: icon,
  sound: new Audio(sound),
};

export const useBrowserNotificationStore = defineStore(StateNamespace.BROWSER_NOTIFICATION, {
  state: () => ({ ...state }),
  getters: {
    isEnabled(): boolean {
      const userStore = useUserStore();

      return userStore.isUserNotificationsActive;
    },

    isAvailable(): boolean {
      const userStore = useUserStore();

      if ("Notification" in window) {
        return (
          userStore.isUserNotificationsActive && Notification.permission === NOTIFICATION_PERMISSION.GRANTED
        );
      }

      return false;
    },

    isGranted(): boolean {
      if ("Notification" in window) {
        return Notification && Notification.permission === NOTIFICATION_PERMISSION.GRANTED;
      }

      return false;
    },
  },

  actions: {
    requestPermissions(canNotify = true): Promise<NOTIFICATION_PERMISSION> {
      const userStore = useUserStore();
      const appStore = useAppStore();

      if ("Notification" in window) {
        return Notification.requestPermission().then((res) => {
          if (res === NOTIFICATION_PERMISSION.GRANTED) {
            userStore.updateNotificationUser(true, canNotify);
          } else {
            appStore.showSnackbar({
              message: "settings.notification.denied_permissions_push_notification",
              type: NotificationType.ERROR,
            });
          }

          return res;
        }) as Promise<NOTIFICATION_PERMISSION>;
      }

      return Promise.resolve(NOTIFICATION_PERMISSION.DENIED);
    },

    notify(notificationConfig: LocalNotification): Promise<Notification | string> {
      if ("Notification" in window) {
        return new Promise((resolve, reject) => {
          if (!document.hidden) {
            reject("Only foreground notification available");
          } else if (!this.isAvailable) {
            this.requestPermissions(false).then((permission) => {
              this.notifyWithPermissions(notificationConfig, permission as NOTIFICATION_PERMISSION)
                .then(resolve)
                .catch(reject);
            });
          } else {
            this.notifyWithPermissions(notificationConfig, Notification.permission as NOTIFICATION_PERMISSION)
              .then(resolve)
              .catch(reject);
          }
        });
      }

      return Promise.resolve(NOTIFICATION_PERMISSION.DENIED);
    },

    notifyWithPermissions(
      notificationConfig: LocalNotification,
      permission: NOTIFICATION_PERMISSION
    ): Promise<Notification | string> {
      if ("Notification" in window) {
        return new Promise((resolve, reject) => {
          if (permission === NOTIFICATION_PERMISSION.GRANTED) {
            try {
              const notification = new Notification(notificationConfig.title, {
                body: notificationConfig.body,
                icon: this.iconPath,
              });
              const chatStore = useChatStore();

              if (hasValue(this.sound)) {
                try {
                  this.sound.play().catch((e) => {
                    log("Error playing the call audio", e);
                  });
                } catch (e) {
                  log("Not play audio");
                }
              }

              notification.onclick = function () {
                window.focus();

                if (hasValue(notificationConfig.redirect)) {
                  if (notificationConfig.redirect.name === APP_ROUTES.chat.root.name) {
                    const patientUuid = isValidString(notificationConfig.extraParams)
                      ? notificationConfig.extraParams
                      : "";
                    const nextActiveRoom = chatStore.getRoomIdFromPatientUuid(patientUuid);

                    chatStore.setActiveChatRoom(nextActiveRoom);
                  }

                  AppRouter.push(notificationConfig.redirect);
                }
              };

              resolve(notification);
            } catch (e) {
              reject("Error launch notification");
            }
          } else {
            Promise.resolve(NOTIFICATION_PERMISSION.DENIED);
          }
        });
      } else {
        return Promise.resolve(NOTIFICATION_PERMISSION.DENIED);
      }
    },
  },
});

export type BrowserNotificationStore = ReturnType<typeof useBrowserNotificationStore>;

export default useBrowserNotificationStore;
